import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo
} from "react";
import { debounce } from "lodash";
import {
  Container,
  MobileSearchInput,
  Results,
  StyledSearch as Search,
  SpinnerWrapper,
  AutocompleteContainer,
  InputWrapper,
  PlaceholderOverlay
} from "./styles";
import AutoComplete from "../AutoComplete";
import { SearchContext } from "../../context";
import { Input } from "antd";
import Icon from "../Icon";
import { useTheme } from "styled-components";
import { CustomSpinner } from "../SpinnerSmall/styles";
import { capitalizeFirstWord } from "../../utils/helpers";
import { NATURAL_LANGUAGE_WORD_THRESHOLD } from "./constants";
import { getModeIcon, filterByVisibility } from "./utils";
import { ENTITY_BY_FILTER_NAME } from "../../constants/entityTypes";
import {
  SEARCH_CHIP_CONFIG,
  SEARCH_MODES,
  UPLOAD_GATE_REASON
} from "./constants";
import { useHoverDelay } from "./hooks";
import ModeSwitcher from "./ModeSwitcher";
import SearchHintRow from "./SearchHintRow";
import SwapText from "./SwapText";
import UploadModal from "./UploadModal";
import UploadAudioButton from "./UploadAudioButton";
import AnalyzingPill from "./AnalyzingPill";
import { MessageContext } from "../../context/MessageContext";
import { AuthContext } from "../../context/AuthProvider";

const Searchbar = ({ mobile, toggleSearchBar }) => {
  const {
    suggestions,
    fetchSearchResult,
    searchValue,
    setSearchValue,
    getSuggestions,
    fetchEntitySuggestions,
    tagSearchResults,
    instrumentSearchResults,
    genreSearchResults,
    setIsSearching,
    aggregateResults,
    selectedTrackList,
    setSelectedTrackList,
    exitEntityToBrowse,
    isLoadingAutocomplete,
    setLoadingAutocomplete,
    clearSuggestions,
    queryAlbumDetail,
    queryPlaylistDetail,
    fetchTrack,
    setPendingWidgetEntity,
    runNaturalLanguageSearch,
    uploadedTrack,
    setUploadedTrack,
    searchMode: mode,
    setSearchMode: setMode
  } = useContext(SearchContext);

  const allSuggestions = useMemo(
    () => [
      ...suggestions,
      ...filterByVisibility(tagSearchResults),
      ...filterByVisibility(genreSearchResults),
      ...instrumentSearchResults,
      ...aggregateResults
    ],
    [
      suggestions,
      tagSearchResults,
      genreSearchResults,
      instrumentSearchResults,
      aggregateResults
    ]
  );

  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [textSearching, setTextSearching] = useState(true);

  // local search value for no lagginess in search typing
  const [value, setValue] = useState(searchValue);
  useEffect(() => {
    setValue(searchValue);
  }, [searchValue]);

  // Stable debounced commit: ref always points at the latest logic, so the
  // debounce timer survives re-renders
  const commitRef = useRef();
  commitRef.current = newValue => {
    setLoadingAutocomplete(true);
    setSearchValue(newValue);
    getSuggestions(newValue);
  };
  const debouncedCommit = useMemo(
    () => debounce(newValue => commitRef.current(newValue), 300),
    []
  );
  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

  const clearInput = () => {
    debouncedCommit.cancel();
    setValue("");
    setSearchValue("");
    setLoadingAutocomplete(false);
    // Drop stale suggestions so a fast Enter after clearing can't re-select a
    // previous query's entity before fresh suggestions load
    clearSuggestions();
  };

  const [placeholder, setPlaceholder] = useState({ text: "", key: "" });

  // input state
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isSearchHovered, setIsSearchHovered] = useState(false);

  // upload/analyzed state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const isAnalyzing = uploadedTrack.status === "analyzing";
  const isAnalyzed = uploadedTrack.status === "analyzed";
  const isUploadActive = isAnalyzing || isAnalyzed;

  const { showMessage } = useContext(MessageContext);
  const auth = useContext(AuthContext);

  // only allow upload for logged in users
  const canUpload = auth?.user?.role === "private";

  const uploadHint = useHoverDelay();

  const handleUploadClick = () => {
    if (!canUpload) return;
    setUploadModalOpen(true);
  };

  const handleUploadSubmit = file => {
    // Upload similarity should run against the full library, drop any
    // album/playlist/similar-track context (and its URL params) before analyzing
    if (selectedTrackList) exitEntityToBrowse();
    setMode(SEARCH_MODES.SIMILARITY);
    setUploadedTrack({ status: "analyzing", filename: file.name, file });
    setUploadModalOpen(false);
  };

  useEffect(() => {
    // skip the success toast when the upload was restored from a parked entity
    if (uploadedTrack.status === "analyzed" && !uploadedTrack.restored) {
      setMode(SEARCH_MODES.SEARCH);
      const count = uploadedTrack.resultCount ?? 0;
      showMessage({
        type: "success",
        duration: 5000,
        heading: "Track analyzed successfully!",
        description: `Found ${count} similar track${count === 1 ? "" : "s"}`
      });
    } else if (uploadedTrack.status === "failed") {
      setMode(SEARCH_MODES.SEARCH);
      showMessage({
        type: "error",
        duration: 6000,
        heading: "Couldn't analyze that track",
        description: uploadedTrack.error || "Please try another file."
      });
      setUploadedTrack({ status: "idle", filename: null });
    }
  }, [
    uploadedTrack.status,
    uploadedTrack.resultCount,
    uploadedTrack.error,
    uploadedTrack.restored,
    showMessage,
    setMode,
    setUploadedTrack
  ]);

  const showHints = !isInputFocused && isSearchHovered;
  const searchRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const handleOutsideClick = e => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Dynamic search icon based on input and suggestions
  const modeIcon = useMemo(() => getModeIcon(mode, value, allSuggestions), [
    mode,
    value,
    allSuggestions
  ]);

  // Auto-highlight when there's an exact title match or only one suggestion
  useEffect(() => {
    if (!showSuggestions || !value) return;

    const exactMatchIndex = allSuggestions.findIndex(
      item =>
        (item.name || item.title || "").toLowerCase() === value.toLowerCase()
    );

    if (exactMatchIndex !== -1) {
      setActiveSuggestion(exactMatchIndex + 1);
      setTextSearching(false);
      return;
    }

    if (allSuggestions.length === 1) {
      setActiveSuggestion(1);
      setTextSearching(false);
      return;
    }

    setActiveSuggestion(0);
    setTextSearching(true);
  }, [allSuggestions, showSuggestions, value]);

  const autocompleteDominant = showSuggestions && value.length > 0;

  const handleChange = e => {
    const newValues = capitalizeFirstWord(e.target.value);

    setValue(newValues);
    setActiveSuggestion(0);
    setTextSearching(true);

    if (newValues.length === 0) {
      clearInput();
      setShowSuggestions(false);
      return;
    }

    // Everything above is local state (instant). The context write, spinner,
    // and autocomplete query are all deferred to the debounced commit
    setShowSuggestions(true);
    debouncedCommit(newValues);
  };

  const handleKeyDown = useCallback(
    (e, autoCompleteKeyDown) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestions(false);
        setActiveSuggestion(-1);
        setLoadingAutocomplete(false);
        e.target.blur();
        return;
      } else if (autoCompleteKeyDown) {
        autoCompleteKeyDown(e);
      }
    },
    [setShowSuggestions, setActiveSuggestion, setLoadingAutocomplete]
  );

  // create a mutable ref to handler that gets refreshed with up-to-date redux store every rerender
  // this is necessary because of useEffect dependencies & redux
  // if you add songTime to useEffect dependencies the event listeners get added & removed way too often
  const handleKeyDownRef = useRef(handleKeyDown);
  useEffect(() => {
    handleKeyDownRef.current = handleKeyDown;
  }, [handleKeyDown]);

  useEffect(() => {
    const keyDownHandler = e => handleKeyDownRef.current(e);
    document.addEventListener("keydown", keyDownHandler);
    return () => document.removeEventListener("keydown", keyDownHandler);
  }, []);

  // Search on selectable (item selected from dropdown)
  const runFilterSearch = item => {
    clearInput();
    if (["track", "session", "playlist"].includes(item.type)) {
      redirect(item);
    } else {
      // A filter item (genre/instrument/tag/aggregate) filters within the current
      // album/playlist; from a single track it starts a fresh search instead
      fetchSearchResult(item.name, item.type, item);
    }
  };

  // Normalize for comparison
  const normalizeForMatch = str =>
    (str || "")
      .toLowerCase()
      .replace(/[^\w\s]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const normalizedValue = normalizeForMatch(value);

  // split autocomplete results into exact and prefix matches
  // --> prefix matches are used to avoid defaulting to NL search when user is looking for a title
  const getTitleMatches = list => {
    if (!normalizedValue) return { exact: [], prefix: [] };
    const exact = list.filter(
      x =>
        normalizeForMatch(x.title) === normalizedValue ||
        normalizeForMatch(x.name) === normalizedValue
    );
    const prefix = list.filter(
      x =>
        normalizeForMatch(x.title).startsWith(normalizedValue) ||
        normalizeForMatch(x.name).startsWith(normalizedValue)
    );
    return { exact, prefix };
  };

  const onEnter = async item => {
    setShowSuggestions(false);
    if (value.length === 0) return;

    // suggestion object was clicked/selected from the dropdown
    if (typeof item !== "string") {
      runFilterSearch(item);
      return;
    }

    const wordCount = value
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    const belowNlThreshold = wordCount < NATURAL_LANGUAGE_WORD_THRESHOLD;

    let matches = getTitleMatches(suggestions);
    if (!belowNlThreshold && matches.prefix.length === 0) {
      // before we commit to NL search, check title matches against fresh suggestions
      // - prevent fast type-and-Enter from showing wrong results
      debouncedCommit.cancel();
      const fresh = await fetchEntitySuggestions(value);
      matches = getTitleMatches(fresh);
    }

    if (matches.exact.length === 1) {
      // typed the exact title of a single entity -> jump straight to it
      redirect(matches.exact[0]);
    } else if (matches.prefix.length > 0 || belowNlThreshold) {
      // title match (or short query) -> ES text search surfaces tracks
      fetchSearchResult(value);
      clearInput();
    } else {
      // No title starts with/matches the query -> treat it as natural language.
      runNaturalLanguageSearch(value);
      clearInput();
    }
  };

  const scrollBrowseToTop = () => {
    const scrollContainer = document.querySelector(
      '[class*="BrowseContentContainer"]'
    );
    if (scrollContainer)
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isAlreadySelected = item => {
    const current = selectedTrackList;
    if (!current?.entity) return false;
    return String(current.entity.id) === String(item.id);
  };

  const redirect = item => {
    if (isAlreadySelected(item)) {
      clearInput();
      scrollBrowseToTop();
      return;
    }

    // Cleared only once we're committed to navigating
    setIsSearching(false);

    if (item.__typename === "Track") {
      // Autocomplete items lack mp3File (and other full-track fields), so fetch
      // the complete track before selecting it — otherwise play actions build
      // `${FILES_URL}/undefined` and 404. Show a skeleton while it loads.
      setPendingWidgetEntity({ __typename: "Track", id: item.id });
      fetchTrack(item.id).then(track => {
        if (track) {
          setSelectedTrackList({
            name: track.title || track.name,
            type: "Track",
            entity: track
          });
        }
        setPendingWidgetEntity(null);
      });
    } else if (item.__typename === "Album") {
      setPendingWidgetEntity({
        __typename: "Album",
        id: item.id,
        name: item.name,
        description: item.description,
        thumbnail: item.thumbnail,
        created_at: item.created_at
      });
      queryAlbumDetail(item);
    } else if (item.__typename === "Playlist") {
      setPendingWidgetEntity({
        __typename: "Playlist",
        id: item.id,
        name: item.name,
        description: item.description,
        thumbnail: item.thumbnail
      });
      queryPlaylistDetail(item);
    }
    clearInput();
  };

  const renderOption = ({ iconName, item, type, index }) => {
    return (
      <div
        onClick={() => {
          onEnter(item, type);
          if (mobile) toggleSearchBar(false);
        }}
        className={`result ${
          index === activeSuggestion ? "focused-result" : ""
        }`}
        key={index}
      >
        <Icon
          type={iconName}
          size="sm"
          color={theme.textLight}
          className="result-icon"
          hoverBackground={false}
        />
        <p className="name">{item.name}</p>
        <p className="type">{type}</p>
      </div>
    );
  };

  const renderAutocompleteOptions = suggestionsAll => {
    let count = 0;

    if (isLoadingAutocomplete && value.length > 0) {
      return (
        <AutocompleteContainer>
          <Results className="RESULTS CONTAINER">
            <SpinnerWrapper>
              <CustomSpinner />
            </SpinnerWrapper>
          </Results>
        </AutocompleteContainer>
      );
    }

    return (
      <AutocompleteContainer>
        <Results>
          {suggestionsAll.map(item => {
            const config =
              ENTITY_BY_FILTER_NAME[item.type] ?? SEARCH_CHIP_CONFIG[item.type];
            if (!config) {
              return null;
            }
            count++;
            const { iconName, displayName } = config;
            return renderOption({
              iconName,
              item,
              type: displayName,
              index: count
            });
          })}
        </Results>
      </AutocompleteContainer>
    );
  };

  const shouldShowAutocomplete = (suggestionsAll, isMobile) =>
    (isLoadingAutocomplete && value.length > 0) ||
    (showSuggestions &&
      value.length > 0 &&
      (suggestionsAll.length > 0 || isMobile));

  return (
    <Container ref={searchRef}>
      <AutoComplete
        value={value}
        suggestions={allSuggestions}
        onEnter={onEnter}
        activeSuggestion={activeSuggestion}
        setActiveSuggestion={setActiveSuggestion}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        textSearching={textSearching}
        setTextSearching={setTextSearching}
      >
        {(onKeyDown, suggestionsAll) => {
          const isFocused = showSuggestions || value.length > 0;
          /** Desktop view */
          if (!mobile) {
            return (
              <div
                className="search"
                onMouseEnter={() => {
                  setIsSearchHovered(true);
                  uploadHint.open();
                }}
                onMouseLeave={() => {
                  setIsSearchHovered(false);
                  uploadHint.scheduleClose();
                }}
              >
                {mode === SEARCH_MODES.SIMILARITY && !isUploadActive && (
                  <UploadAudioButton
                    onClick={handleUploadClick}
                    visible={uploadHint.isOpen}
                    onMouseEnter={uploadHint.open}
                    onMouseLeave={uploadHint.scheduleClose}
                    disabled={!canUpload}
                    disabledReason={UPLOAD_GATE_REASON}
                  />
                )}
                <Search $isFocused={isFocused}>
                  <ModeSwitcher
                    mode={mode}
                    modeIcon={modeIcon}
                    onModeChange={setMode}
                    forceClosed={autocompleteDominant || isAnalyzing}
                  />
                  {isAnalyzing ? (
                    <AnalyzingPill fill />
                  ) : (
                    <InputWrapper>
                      <input
                        className="input"
                        data-hj-allow
                        value={value}
                        onChange={handleChange}
                        placeholder=""
                        onKeyDown={e => handleKeyDown(e, onKeyDown)}
                        onFocus={() => {
                          setIsInputFocused(true);
                          if (value.length > 0) setShowSuggestions(true);
                        }}
                        onBlur={() => setIsInputFocused(false)}
                      />
                      {value.length === 0 && !isInputFocused && (
                        <PlaceholderOverlay>
                          <SwapText inline swapKey={placeholder.key}>
                            {placeholder.text}
                          </SwapText>
                        </PlaceholderOverlay>
                      )}
                    </InputWrapper>
                  )}
                </Search>
                {!shouldShowAutocomplete(suggestionsAll) && !isUploadActive && (
                  <SearchHintRow
                    mode={mode}
                    inputValue={value}
                    isFocused={isInputFocused}
                    visible={showHints}
                    onModeChange={setMode}
                    onUploadClick={handleUploadClick}
                    setPlaceholder={setPlaceholder}
                    uploadDisabled={!canUpload}
                    uploadDisabledReason={UPLOAD_GATE_REASON}
                  />
                )}
                {shouldShowAutocomplete(suggestionsAll) &&
                  renderAutocompleteOptions(suggestionsAll)}
              </div>
            );
          }
          return (
            /** Mobile view */
            <div>
              <MobileSearchInput>
                {mode === SEARCH_MODES.SIMILARITY && !isUploadActive && (
                  <UploadAudioButton
                    onClick={handleUploadClick}
                    visible
                    disabled={!canUpload}
                    disabledReason={UPLOAD_GATE_REASON}
                  />
                )}
                <ModeSwitcher
                  mode={mode}
                  modeIcon={modeIcon}
                  onModeChange={setMode}
                  mobile
                  forceClosed={autocompleteDominant || isAnalyzing}
                  outsideClickRef={searchRef}
                />
                {isAnalyzing ? (
                  <AnalyzingPill fill />
                ) : (
                  <Input
                    placeholder={placeholder.text}
                    autoFocus
                    className="focus-visible"
                    data-hj-allow
                    value={value}
                    onChange={handleChange}
                    onKeyDown={e => {
                      handleKeyDown(e, onKeyDown);
                      if (e.key === "Enter") toggleSearchBar(false);
                    }}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                  />
                )}
                <Icon
                  type="close"
                  size="sm"
                  color="white"
                  label="close-btn"
                  className="close-icon"
                  onClick={() => toggleSearchBar(false)}
                />
              </MobileSearchInput>
              {!shouldShowAutocomplete(suggestionsAll) && !isUploadActive && (
                <SearchHintRow
                  mode={mode}
                  inputValue={value}
                  isFocused={isInputFocused}
                  visible={showHints}
                  onModeChange={setMode}
                  onUploadClick={handleUploadClick}
                  setPlaceholder={setPlaceholder}
                  uploadDisabled={!canUpload}
                  uploadDisabledReason={UPLOAD_GATE_REASON}
                />
              )}
              {shouldShowAutocomplete(suggestionsAll) &&
                renderAutocompleteOptions(suggestionsAll)}
            </div>
          );
        }}
      </AutoComplete>
      <UploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUploadSubmit}
      />
    </Container>
  );
};

export default Searchbar;
